import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { PetlService } from '../../../services/petl.service';
import { PetlBaseComponent } from '../petl-base.component';
import { IProject, IScenario } from '../petl-types';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-petl-processor-ng',
    styleUrls: ['../../sharedStyles/base.scss', '../petl-base.scss', './processor.component.scss'],
    templateUrl: './processor.component.html'
})

export class PetlProcessorComponent extends PetlBaseComponent implements OnInit, OnDestroy {

    advanced?: boolean;
    append?: boolean;
    createProject?: boolean = true;
    delimiter?: string;
    fileExtension?: string;
    fileUploadedSubscription: Subscription;
    fileName?: string;
    lastCreatedProject: IProject;
    newProject: IProject;
    newProjectLabel = 'New Project';
    newScenario: IScenario;
    newScenarioLabel = 'New Scenario';
    originalDescription?: string;
    projectList?: IProject[] = [];
    scenario: any;
    simpleScenarioPrefix = 'Scenario';
    specificHelp?: string;
    specificHelpShown = null;
    unusedFieldsMarginLeft = 0;

    @ViewChild('petlColumnMapping') private readonly petlColumnMapping: any;

    constructor(_vcr: ViewContainerRef, protected readonly petlService: PetlService, protected readonly cdr: ChangeDetectorRef) {
        super(_vcr, petlService, cdr);
    }

    get project() {
        return this.service.project;
    }
    set project(value: IProject) {
        this.service.project = value;
    }

    async ngOnInit() {
        if (this.petlService.project) {
            this.startNewFile();
        }
        this.fileUploadedSubscription = this.petlService.fileUploaded.subscribe(() => {
            this.startNewFile();
        });
    }

    ngOnDestroy() {
        this.fileUploadedSubscription.unsubscribe();
    }

    get enableValidateButton() {
        return this.projectDescriptionIsValid && this.petlColumnMapping.geometryIsValid() && (this.scenarioIsValid() || !this.advanced);
    }

    get projectDescriptionIsValid() {
        return this.project && this.project.description !== this.newProjectLabel && this.project.description && (!this.createProject || !this.projectDescriptionExistsInAnotherProject);
    }

    get willOverwriteScenario() {
        let willOverwrite = this.project !== null && this.enableValidateButton && this.project.scenarios && this.project.scenarios.length > 0;
        willOverwrite = willOverwrite && (!this.advanced && !this.append) || (this.scenario && this.scenario.id);
        return willOverwrite;
    }

    get projectDescriptionExistsInAnotherProject() {
        if (!this.projectList || !this.project) {
            return false;
        }
        const arr = this.projectList.filter((project) => {
            return (project.originalDescription === this.project.description && project.id !== this.project.id);
        });
        return arr.length > 0;
    }

    async startNewFile() {
        this.initializeVariables();
        this.initializeScenario();
        this.createNewProject();
        this.resetNewScenario();
        await this.refreshProjectList();
        this.project = this.newProject;
        this.setProjectListFromServiceProjectList();
        this.createProject = !this.setExistingProjectIfIdOrFilenameMatches();
        await this.projectChanged();
        // this.cdr.detectChanges();
        this.petlService.fileDropped = false;
    }

    async projectChanged() {
        this.resetNewScenario();
        this.petlService.resetAliases();
        if (this.project && this.project.id) {
            const fullProject = await this.petlService.getProject(this.project.id);
            if (fullProject) {
                this.project.columnAliases = fullProject.columnAliases;
                this.petlService.updateColumnAliasesFromProject();
            }
            this.petlColumnMapping.refresh();
        }
    }

    async processMapping() {
        if (!this.scenario) {
            this.initializeScenario();
        }
        this.fileName = this.petlService.fileName;
        this.fileExtension = this.petlService.getFileExtension();
        this.delimiter = ',';
        // Sets the project id from the service project id. TODO Reconsider the placement of this code.
        if (this.petlService.project.id && !this.project.id) {
            this.project.id = this.petlService.project.id;
        }
        this.refreshColumnMapping();
        await this.petlService.sendProcessForm(this);
        this.project = this.petlService.project;
    }

    async validate() {
        if (this.createProject) {
            this.project = this.newProject;
        }
        this.updateProjectAliasesBasedOnMapping();
        this.project = await this.petlService.addOrUpdateProject();
        this.createProject = false;
        this.lastCreatedProject = this.project;
        this.processMapping();
    }

    initializeVariables() {
        this.newProjectLabel = 'New Project';
        this.newScenarioLabel = 'New Scenario';
        this.simpleScenarioPrefix = 'Scenario';
        this.advanced = false;
        this.append = false;
        this.createProject = true;
        this.project = null;
        this.petlColumnMapping.initialize();
    }

    createNewProject() {
        this.newProject = {};
        this.newProject.id = null;
        this.newProject.selectLabel = this.newProjectLabel;
        const fileExtensions = ['csv', 'xlsx', 'xls'];
        let newProjectDescription = '';
        if (this.petlService.fileName) {
            newProjectDescription = this.petlService.fileName;
        }
        for (const fileExension of fileExtensions) {
            const exension = '.' + fileExension;
            newProjectDescription = newProjectDescription.replace(exension, '').replace(exension.toUpperCase(), '');
        }
        this.newProject.description = newProjectDescription;
        const lifeSpan = 60;
        this.newProject.lifeSpanDays = lifeSpan;
        this.newProject.columnAliases = [];
    }

    async redefineStartingRowNumber() {
        await this.petlService.redefineStartingRowNumber();
    }

    initializeScenario() {
        this.newScenario = {};
        this.newScenario.id = null;
        this.newScenario.selectLabel = this.newScenarioLabel;
        this.newScenario.description = 'Scenario 1';
        this.scenario = this.newScenario;
    }

    resetNewScenario() {
        if (!this.newScenario) {
            return;
        }
        this.scenario = this.newScenario;
        if (!this.advanced) {
            if (this.append) {
                this.newScenario.description = this.project.nextSimpleScenarioName;
            } else {
                this.newScenario.description = this.simpleScenarioPrefix + ' 1';
            }
        } else {
            this.newScenario.description = '';
            if (this.project.scenarios.length > 0) {
                this.scenario = this.project.scenarios[0];
            } else if (this.project.selectScenarios.length > 0) {
                this.scenario = this.project.selectScenarios[0];
            }
        }
    }

    async refreshProjectList() {
        await this.petlService.getProjectList();
    }

    setProjectListFromServiceProjectList() {
        if (!this.projectList) {
            this.projectList = [];
        }
        this.projectList.length = 0;
        for (const project of this.petlService.projectList) {
            project.originalDescription = project.description;
            project.selectLabel = `${project.id.toString()} - ${project.description}`;
            this.populateProjectScenarioList(project);
            this.setNewSimpleScenarioNameForProject(project);
            this.projectList.push(project);
        }
    }

    setExistingProjectIfIdOrFilenameMatches() {
        let found = false;
        for (const project of this.projectList) {
            if (this.petlService.project && project.id === this.petlService.project.id) {
                this.project = project;
                return true;
            } else if (this.newProject && project.description === this.newProject.description) {
                this.project = project;
                found = true;
            }
        }
        return found;
    }

    scenarioNameExists(testProject: IProject = null, testDescription: string = null) {
        if (!testProject) {
            testProject = this.project;
        }
        if (!testDescription) {
            testDescription = this.scenario.description;
        }
        if (!testProject || !testProject.scenarios) {
            return false;
        }
        const arr = testProject.scenarios.filter((scenario) => {
            return (scenario.description === testDescription && (!this.scenario || scenario.id !== this.scenario.id));
        });
        return arr.length > 0;
    }

    populateProjectScenarioList(project) {
        project.selectScenarios = [];
        project.selectScenarios.push(this.newScenario);
        if (project.scenarios) {
            for (const scenario of project.scenarios) {
                scenario.selectLabel = scenario.description;
                project.selectScenarios.push(scenario);
            }
        } else {
            project.scenarios = [];
        }
    }

    setNewSimpleScenarioNameForProject(project) {
        let i = 1;
        const limit = 100;
        while (!project.nextSimpleScenarioName && i < limit) {
            const possibleUnqiueName = `${this.simpleScenarioPrefix.toString()} ${i.toString()}`;
            if (!this.scenarioNameExists(project, possibleUnqiueName)) {
                project.nextSimpleScenarioName = possibleUnqiueName;
            }
            i++;
        }
    }

    startOver() {
        this.initializeScenario();
        this.petlService.startOver();
    }

    scenarioIsValid() {
        return this.project && this.scenario && this.scenario.description && (this.scenario.id !== null || this.scenarioNameIsValid());
    }

    scenarioNameIsValid() {
        return this.scenario.description !== this.newScenarioLabel && !this.scenarioNameExists();
    }

    refreshColumnMapping() {
        this.petlColumnMapping.refreshColumnMapping();
    }

    toggleSpecificHelp(specificHelp) {
        if (this.specificHelpShown === specificHelp) {
            this.specificHelpShown = null;
        } else {
            this.specificHelpShown = specificHelp;
        }
    }

    closeHelp() {
        this.specificHelp = null;
    }

    toggleNewProject() {
        this.createProject = !this.createProject;
        if (this.createProject) {
            this.project = this.newProject;
        }
    }

    updateProjectAliasesBasedOnMapping() {
        this.project.columnAliases = [];
        this.petlService.columnData.forEach((val) => {
            if (val.databaseField && val.databaseField.aliasName) {
                this.project.columnAliases.push({ columnName: val.databaseField.databaseName, aliasName: val.databaseField.aliasName });
            }
        });
    }

    checkLifeSpan() {
        if (this.project.lifeSpanDays < 0) {
            this.project.lifeSpanDays = 0;
        } else if (this.project.lifeSpanDays > 365) {
            this.project.lifeSpanDays = 365;
        }
    }
}
